import { Calendar, CalendarChangeEvent, ToggleButton } from '@progress/kendo-react-dateinputs';
import { InputClearValue, InputSuffix, TextBox, TextBoxChangeEvent, TextBoxHandle, TextBoxProps } from '@progress/kendo-react-inputs';
import { Popup } from '@progress/kendo-react-popup';
import { AsyncFocusBlur } from '@progress/kendo-react-common';
import dayjs from 'dayjs';
import * as React from 'react';
import { useState } from 'react';
import { SvgIcon } from "@progress/kendo-react-common";
import { calendarIcon, xIcon } from "@progress/kendo-svg-icons";

export interface SimpleDatePickerProps {
  required?: boolean;
  onChange: (value: Date | null) => void;
  value: Date | null;
  inputProps?: TextBoxProps;

  //wip
  min?: Date;
  max?: Date;
}

const currentFormat = "MM/DD/YYYY";

const getFormattedDate = (value: string): dayjs.Dayjs | null => {
  const formats = ["MM/DD/YYYY", "M/D/YYYY", "M/DD/YYYY", "MM/D/YYYY", "MMDDYYYY"];

  if (dayjs(value, formats, true).isValid()) {
    return dayjs(value, formats, true);
  }

  if (value?.length === 6 && dayjs(`0${value[0]}0${value[1]}${value.substring(2)}`, "MMDDYYYY", true).isValid())
    return dayjs(`0${value[0]}/0${value[1]}/${value.substring(2)}`, currentFormat, true);

  return null;
};

interface DatePickerSuffixContextValue {
  inputValue: string;
  setInputValue: React.Dispatch<React.SetStateAction<string>>;
  onChange: SimpleDatePickerProps['onChange'];
  anchor: React.RefObject<TextBoxHandle>;
  popupVisible: boolean;
  setPopupVisible: React.Dispatch<React.SetStateAction<boolean>>;
  calendarValue: Date | null;
  handleDateSelect: (e: CalendarChangeEvent) => void;
  min?: Date;
  max?: Date;
}

const DatePickerSuffixContext = React.createContext<DatePickerSuffixContextValue | null>(null);

// TextBox mounts its suffix as a component, so keep this type stable while browsing the calendar.
const DatePickerSuffix = () => {
  const context = React.useContext(DatePickerSuffixContext);
  const calendarFocusPrimed = React.useRef(false);
  const [, refreshCalendar] = React.useState(0);

  React.useEffect(() => {
    if (!context?.popupVisible) calendarFocusPrimed.current = false;
  }, [context?.popupVisible]);

  if (!context) return null;

  const { inputValue, setInputValue, onChange, anchor, popupVisible, setPopupVisible, calendarValue, handleDateSelect, min, max } = context;

  const primeCalendarFocus = () => {
    if (calendarFocusPrimed.current) return;
    calendarFocusPrimed.current = true;
    // Kendo needs a render after focus before its side-month navigation can change the view.
    refreshCalendar(current => current + 1);
  };

  return <>
    {inputValue?.length > 0 &&
      <InputClearValue onClick={() => { setInputValue(''); onChange(null); anchor.current?.element?.focus(); }}>
        <SvgIcon icon={xIcon} />
      </InputClearValue>
    }
    <InputSuffix>
      <ToggleButton
        type="button"
        tabIndex={-1}
        icon="calendar"
        svgIcon={calendarIcon}
        rounded={null}
        title="Toggle calendar"
        className="k-input-button"
        onClick={() => {
          setPopupVisible(!popupVisible);
          // On popup close, focus the input
          if (popupVisible) anchor.current?.element?.focus();
        }}
        aria-label="Toggle calendar"
      />
    </InputSuffix>
    <Popup
      anchor={anchor.current?.element}
      show={popupVisible}
      popupClass="popup-content"
      collision={{
        horizontal: "fit",
        vertical: "flip",
      }}
      animate={false}
    >
      <Calendar
        onFocus={primeCalendarFocus}
        smoothScroll={false}
        onChange={handleDateSelect}
        value={calendarValue}
        min={min ?? new Date(1753, 1, 1)}
        max={max ?? new Date(2999, 12, 31)}
      />
    </Popup>
  </>;
};

const SimpleDatePicker: React.FC<SimpleDatePickerProps> = ({ onChange, value, inputProps, min, max, required }) => {
  const anchor = React.useRef<TextBoxHandle | null>(null);
  const [inputValue, setInputValue] = useState<string>(value != null ? dayjs(value).format(currentFormat) : '');
  const [calendarValue, setCalendarValue] = useState<Date | null>(value ?? null);
  const [popupVisible, setPopupVisible] = useState(false);
  const previousValueTime = React.useRef(value?.getTime() ?? null);

  const handleChange = (e: TextBoxChangeEvent) => {
    const val = e.value as string;
    if (/^[0-9/]*$/.test(val)) {
      setInputValue(val);
      const formattedDate = getFormattedDate(val);
      if (formattedDate != null) {
        setCalendarValue(formattedDate.toDate());
        onChange(formattedDate.toDate());
      } else {
        onChange(null);
      }
    }
  }

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const formattedDate = getFormattedDate(e.target.value);
    setInputValue(formattedDate != null ? dayjs(formattedDate).format(currentFormat) : value != null ? dayjs(value).format(currentFormat) : '');
    if (popupVisible) setPopupVisible(false);
  }

  const handleDateSelect = (e: CalendarChangeEvent) => {
    const calendarValue = e.value;

    setCalendarValue(calendarValue);
    setInputValue(dayjs(calendarValue).format(currentFormat));
    setPopupVisible(false);
    onChange(calendarValue);
  }

  React.useEffect(() => {
    const valueTime = value?.getTime() ?? null;
    if (previousValueTime.current === valueTime) return;
    previousValueTime.current = valueTime;

    if (anchor?.current?.element != document.activeElement) {
      setCalendarValue(value);
      setInputValue(value != null ? dayjs(value).format(currentFormat) : '');
    }
  }, [value]);

  return <DatePickerSuffixContext.Provider value={{ inputValue, setInputValue, onChange, anchor, popupVisible, setPopupVisible, calendarValue, handleDateSelect, min, max }}>
    <AsyncFocusBlur
      onFocus={(e) => {
        // Focusing the calendar button should not open the popup before its click toggles it.
        if (e.target !== anchor.current?.element) return;
        anchor.current.element.select();
        setPopupVisible(true);
      }}
      onBlur={handleBlur}
    >
      {({ onFocus, onBlur }) => (<>
        <TextBox
          {...inputProps}
          style={{ minWidth: 150 }}
          valid={((inputValue?.length === 0 && !required) || getFormattedDate(inputValue) != null) && inputProps?.valid}
          value={inputValue}
          onChange={handleChange}
          onFocus={onFocus}
          ref={anchor}
          onBlur={onBlur}
          maxLength={10}
          suffix={DatePickerSuffix}
        />
      </>)}
    </AsyncFocusBlur>
  </DatePickerSuffixContext.Provider>;
};

export default SimpleDatePicker;
